import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal, Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import styles from './grab_order_popup.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { Actions } from '../../../Actions/Actions';
import api from '../../../seller-apis/api';
import { useAlert } from 'react-alert';
import { fetchOpportunityData } from '../../../seller-apis/apidataField';

// TimerProgress component
const TimerProgress = ({ timerDuration, onClose, isVisible }) => {
  const [timer, setTimer] = useState(timerDuration);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (isVisible && !isMountedRef.current) {
      setTimer(timerDuration);
      isMountedRef.current = true;
    }
    if (!isVisible) {
      isMountedRef.current = false;
    }
  }, [isVisible, timerDuration]);

  useEffect(() => {
    if (isVisible && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          const newTimer = prev - 1;
          if (newTimer <= 0) {
            onClose();
          }
          return newTimer;
        });
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [isVisible, onClose]);

  return (
    <div className={styles.progressSection}>
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${((timerDuration - timer) / timerDuration) * 100}%` }}
        ></div>
      </div>
      <div className={styles.progressLabel}>
        Order expires in 0:{timer.toString().padStart(2, '0')}
      </div>
    </div>
  );
};

const MemoizedTimerProgress = React.memo(TimerProgress);

const GrabOrderPopup = (props) => {
  const alert = useAlert();
  const timerDuration = 30;
  const state = useSelector((state) => state.ActionReducer.grabOrderPopupState);
  const dispatch = useDispatch();
  const disableTimer = !!state?.disableTimer;


  const { minPrice, maxPrice, pricesValid } = useMemo(() => {
    const min = parseFloat(state?.data?.expTransferPriceFrom);
    const max = parseFloat(state?.data?.expTransferPriceTo);
    return {
      minPrice: isNaN(min) ? 0 : Math.round(Number(min)),
      maxPrice: isNaN(max) ? 0 : Math.round(Number(max)),
      pricesValid: !isNaN(min) && !isNaN(max),
    };
  }, [state?.data?.expTransferPriceFrom, state?.data?.expTransferPriceTo]);


  const [price, setPrice] = useState(minPrice);
  const [inputValue, setInputValue] = useState(Math.round(minPrice));
  const [inputError, setInputError] = useState('');
  const [isApiCalling, setIsApiCalling] = useState(false);

  useEffect(() => {
    setPrice(Number(minPrice));
    setInputValue(minPrice);
  }, [minPrice]);

  const handleModalClose = useCallback(() => {
    Actions.updateGrabOrderPopupState(dispatch, { visible: false });
  }, [dispatch]);

  const handleSliderChange = (e) => {
    const val = Number(e.target.value);
    setPrice(val);
    setInputValue(val);
    setInputError('');
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (val === '') {
      setInputError('Please enter a number');
      return;
    }
    const numVal = val
    if (isNaN(numVal)) {
      setInputError('Please enter a valid number');
      return;
    }
    const roundedVal = Number(numVal);
    if (roundedVal < minPrice || roundedVal > maxPrice) {
      setInputError(`Value must be between ${minPrice} and ${maxPrice}`);
    } else {
      setInputError('');
      setPrice(roundedVal);
    }
  };

  const handleGrabOrder = async () => {
    const order = state?.data;
    let data = {
      status: 'PENDING',
      _id: order?._id,
      receivedFrom: props?.receivedFrom || 'grab_order',
      orderId: order?.orderId,
      purchase_price: price,
    };
    if (order?.attributes?.length > 0) {
      const combination = {
        url: order?.urlKey,
        purchase_price: data.purchase_price,
      };
      data.combination = combination;
      delete data.purchase_price;
      data.addCombinationFlag = true;
    }
    try {
      setIsApiCalling(true);
      const res = await api.product.addToExProduct(data);
      if (res.message === 'product updated' || res.message === 'product added') {
        alert.success('Order grabbed successfully!');
        updateGrabOrderCount()
        handleModalClose();
      }
    } catch (error) {
      console.error('Error grabbing order:', error);
      alert.error('Failed to grab order.');
    }
    finally{
      setIsApiCalling(false);
    }
  };

  const updateGrabOrderCount = () => {
    // grab order 
    let grabOrderFilterData = {
      pageNo: 1,
      size: 1,
      filters: {
        "status": "pending",
        "orderType": 'grab_order'
      },
      sortFilter: { "createdAt": -1 },
    };

    api.Opportunity.fetchOpportunity(fetchOpportunityData(grabOrderFilterData)).then(res => {
      Actions.GrabOrderCountAction(dispatch, res.totalRecords);
    });
  }


  return (
    <Modal
      show={state?.visible}
      centered
      className={`grab_order_popup ${styles.modal}`}
      backdrop="static"
    >
      <Modal.Header className={styles.modalHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <i className="bi bi-cart3"></i>
            <span className={styles.headerTitle}>New Order Available!</span>
          </div>
        </div>
        <Button variant="link" className={styles.closeBtn} onClick={handleModalClose}>
          <i className="bi bi-x-lg"></i>
        </Button>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <Container fluid>
          {/* Order Item Section */}
          <div className={styles.orderItem}>
            <Row className={styles.productRow}>
              <Col xs={3}>
                <div className={styles.productImage}>
                  <img
                    src={state?.data?.imgUrl?.startsWith("https") ? state?.data?.image : "https://s3.toolsvilla.com/" + state?.data?.image}
                    alt="Product"
                    className={styles.productImg}
                  />
                </div>
              </Col>
              <Col xs={9}>
                <div className={styles.productDetails}>
                  <h6 className={styles.productName}>{state?.data?.productName}</h6>
                  <p className={styles.productSku}>SKU: {state?.data?.SKU}</p>
                  <ul className="d-flex ord-crd mb-0">
                    {state?.data?.attributes ? (
                      JSON.parse(state.data.attributes).map((item, index) => (
                        <li className="bg-theme  p-1 text-white fs-11 corner ff_pop" key={index}>
                          {item} - {JSON.parse(state.data.attributeValue)[index]}
                        </li>
                      ))
                    ) : null}
                  </ul>
                  <p className={styles.quantity}><span className={styles.quantity_span}>Qty : {state?.data?.quantity}</span></p>
                </div>
              </Col>
            </Row>
          </div>

          {/* Price Slider Section */}
          {pricesValid ? (
            <div className={styles.priceSliderSection}>
              <div className={styles.sliderHeader}>
                <span style={{ fontSize: '13px' }}>
                  Expected Price (₹{Math.round(minPrice)} - ₹{Math.round(maxPrice)})
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <div style={{ width: '70%', position: 'relative' }}>
                  <Form.Range
                    min={minPrice}
                    max={maxPrice}
                    value={price}
                    onChange={handleSliderChange}
                    className={styles.priceSlider}
                    step="1"
                  />
                  <div
                    className={styles.sliderValue}
                    style={{
                      position: 'absolute',
                      left: `calc(${(price - minPrice) / (maxPrice - minPrice) * 100}% - 1px)`,
                      top: '-32px',
                      transition: 'left 0.1s',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }}
                  >
                    ₹{Math.round(price)}
                  </div>
                </div>
                <div style={{ width: '30%' }}>
                  <Form.Control
                    type="number"
                    value={inputValue}
                    onChange={handleInputChange}
                    min={minPrice}
                    max={maxPrice}
                    step="1"
                    onWheel={(e) => e.target.blur()}
                    style={{ textAlign: 'center' }}
                    isInvalid={!!inputError}
                  />
                </div>
              </div>
              {inputError && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{inputError}</div>
              )}
              <div className={styles.sliderLabels}>
                <span>Min ₹{Math.round(minPrice)}</span>
                <span>Max ₹{Math.round(maxPrice)}</span>
              </div>
            </div>
          ) : (
            <div style={{ color: 'red', margin: '16px 0' }}>Invalid price range</div>
          )}

          {/* Grab Order Button */}
          <div className={styles.actionSection}>
            <Button
              className={styles.grabOrderBtn}
              onClick={handleGrabOrder}
              disabled={!!inputError || !pricesValid || isApiCalling}
            >
              {isApiCalling ? <><Spinner animation="border"/></> : <><i className="bi bi-check-lg"></i>
              Grab Order at ₹{Math.round(price)}
              </>}
            </Button>
          </div>

          {/* Timer Progress - hidden when disableTimer is true so popup stays available */}
          {!disableTimer && (
            <MemoizedTimerProgress
              timerDuration={timerDuration}
              onClose={handleModalClose}
              isVisible={state?.visible}
            />
          )}
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default React.memo(GrabOrderPopup);